import { ButtonDynamic, ButtonPrimary } from "@nova/component-btn"
import * as Icons from "@nova/component-icons"
import { useAppDispatch, useAppSelector } from "integrations/redux/hooks"
import { useMemo } from "react"
import { activateFlightPath, addUploadTimestamp } from "features/map/layers/slice"
import { GenericUpload, ImageProcessingState, SidePanelLayer } from "@nova/constants"
import { UploadState, useProgressForSingleUploadFlightId } from "@nova/slice-multifile-upload"
import { useUploadFlowContext } from "panels/content/side-panel/upload-flow/useUploadFlowContext"
import { InspectorPanelMetadata } from "features/map/inspector-panel/FlightPanelMetadata"
import { useWorkspacePermissions } from "features/data-management/workspace/useWorkspacePermissions"
import { useEffectUnsafe } from "@nova/use-effect-unsafe"
import { DeleteFlight } from "panels/content/side-panel/upload-flow/components/DeleteFlight"
import { get } from "nodash"
import { trimMiddle } from "@nova/util-strings"
import { activateSidePanelModal } from "panels/content/side-panel/thunks"
import { UploadProgressBar } from "panels/content/side-panel/upload-flow/components/UploadProgressBar"
import { selectUploadTimestampForFlightId } from "features/map/layers/selectors"
import { GenericDataUsage } from "panels/content/side-panel/upload-flow/components/GenericDataUsage"
import { flightExceededUsage } from "panels/content/side-panel/upload-flow/module"

export const PanoramaPreviewFiles = () => {
    const {
        flightId,
        uploads,
        uploadFlight,
        minUploads = 3,
        usableUploads,
        deleteUpload,
        selectFlightById,
        selectIsUploading,
        selectUploadSize,
        selectUploadStateSummary,
        selectUploadsForSetSummary,
    } = useUploadFlowContext()
    const dispatch = useAppDispatch()
    const { isEditor } = useWorkspacePermissions()

    const flight = useAppSelector(selectFlightById(flightId))
    const progress = useProgressForSingleUploadFlightId(flightId)
    const uploadSize = useAppSelector(selectUploadSize(flightId))
    const uploadTimestamp = useAppSelector(selectUploadTimestampForFlightId(flightId))

    const canUpload = uploads.length >= minUploads

    const summary = useAppSelector(selectUploadStateSummary(flightId))
    const warningSummary = useAppSelector(selectUploadsForSetSummary(flightId))
    const isUploading = useAppSelector(selectIsUploading(flightId))

    const canProcess = useMemo(
        () =>
            !!(
                canUpload &&
                uploads.every(
                    (upload) =>
                        "state" in upload &&
                        (upload.state === ImageProcessingState.UploadDone ||
                            upload.state === ImageProcessingState.IngestDone)
                )
            ),
        [uploads, canUpload]
    )

    const stagingDone = useMemo(
        () =>
            !!usableUploads.length &&
            usableUploads.every((upload) => upload.state >= ImageProcessingState.ProcessingDone),
        [usableUploads]
    )

    useEffectUnsafe(() => {
        if (stagingDone) {
            dispatch(
                activateFlightPath({
                    flightId,
                    uploads: uploads as UploadState<{ id: string; error: Error }>[],
                    autoZoom: true,
                    useExistingPath: false,
                })
            )
        }
    }, [stagingDone, dispatch, flightId])

    const upload = uploads[0]

    const hasWarning = !!Object.keys(warningSummary.warnings || {}).length
    const exceededUsage = flightExceededUsage[flightId]
    const hasExceededUploads = !isUploading && exceededUsage > 0

    const fileName = get(upload, "fileName", "")

    return (
        <>
            <div className="border-b border-gray-700" />
            {canUpload && !isUploading && !canProcess && stagingDone && (
                <div className="my-2 flex flex-col gap-2">
                    <div className="flex justify-between">
                        {!hasWarning ? (
                            <>
                                <h5 className="text-gray-0">Ready to Upload</h5>
                                <h5 className="text-gray-100">1 file</h5>
                            </>
                        ) : (
                            <h5 className="text-red-500">Incompatible Panorama File</h5>
                        )}
                    </div>
                    <div className="my-1 flex gap-2 items-center justify-between">
                        <div className="flex gap-2 items-center">
                            {!hasWarning ? (
                                <Icons.SmallIcon
                                    Icon={Icons.Checkmark}
                                    className="text-green-500"
                                />
                            ) : (
                                <Icons.SmallIcon Icon={Icons.Error} className="text-red-500" />
                            )}
                            <small className="text-gray-100 normalLineHeight">
                                {trimMiddle(fileName, { maxLength: 25 })}
                            </small>
                        </div>
                        <Icons.SmallIcon
                            Icon={Icons.Trash}
                            className="text-gray-300 hover:text-red-500 cursor-pointer"
                            onClick={() =>
                                dispatch(deleteUpload({ uploadId: upload.id, setId: flightId }))
                            }
                        />
                    </div>
                </div>
            )}
            {/* Upload Info*/}
            {isUploading && (
                <UploadProgressBar progress={progress} total={1} timeElapsed={uploadTimestamp} />
            )}
            {canProcess && (
                <div className="flex items-center gap-1 my-4">
                    <Icons.Icon Icon={Icons.Checkmark} className="text-green-500" />
                    <h5 className="text-gray-0">Upload Complete</h5>
                </div>
            )}
            {hasExceededUploads && <GenericDataUsage remainingBytes={exceededUsage} />}
            <div className="mt-2">
                <InspectorPanelMetadata
                    entity={flight as SidePanelLayer}
                    uploads={uploads as GenericUpload[]}
                    uploadSize={uploadSize}
                    completedUploadCount={summary.stateCount[ImageProcessingState.UploadDone] || 0}
                    hideCreator
                    className=""
                    isSingleFile
                />
                <div className="border-b border-gray-700" />
                {/* action buttons */}
                {stagingDone && (
                    <ButtonDynamic
                        isTertiary={!canProcess}
                        title={"View Panorama"}
                        RightIcon={Icons.VRPano}
                        className="w-full !px-0 mt-2"
                        onClick={() => {
                            dispatch(
                                activateSidePanelModal({
                                    id: flightId,
                                    type: "layer",
                                    layerContentType: "pano",
                                })
                            )
                        }}
                    />
                )}
                <div className="flex gap-2 items-center mt-2">
                    <DeleteFlight />
                    {!isUploading && !canProcess && (
                        <ButtonPrimary
                            title={"Upload"}
                            className="w-full !px-0"
                            onClick={() => {
                                dispatch(uploadFlight(flightId))
                                dispatch(
                                    addUploadTimestamp({
                                        flightId,
                                        timestamp: new Date().toISOString(),
                                    })
                                )
                            }}
                            disabled={
                                !canUpload ||
                                isUploading ||
                                !isEditor ||
                                hasExceededUploads ||
                                !stagingDone
                            }
                        />
                    )}
                </div>
            </div>
        </>
    )
}
